import React, { useEffect, useRef, useState } from 'react';
import { PhonicsActivity, PhonicsActivityTypeMode } from '@/api/types';
import { useUi, useAssetCdn } from '@/context/Ui';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CdnMedia } from '@/components';
import { Character } from '@/components/lessonKit';
import { CarouselSlide } from '../../Carousel';

const CLAY_FONT = 'var(--clay-font)';
const FLASHCARD_FONT = 'var(--clay-font-flashcard)';

interface ClaySoundStepProps {
  activity: PhonicsActivity;
  mode: PhonicsActivityTypeMode;
  sound: string;
  stepIdentifier: string;
  stepNumber: number;
  totalSteps: number;
  variant: 'grapheme' | 'picture';
  title: string;
  isPreview?: boolean;
}

/**
 * ClaySoundStep — the "Say the Sound" screen from the Claude Design redesign,
 * wired into the Review-Sounds activity. A full-bleed gold clay field with a
 * cream flashcard (grapheme in Sassoon, or the sound's picture), a green clay
 * "hear it" button (real pre-recorded phoneme audio), and Pip pointing calmly
 * from the corner. Registers as a normal slide so player nav/progress/metrics
 * are untouched.
 */
export const ClaySoundStep = ({
  activity,
  sound,
  stepIdentifier,
  stepNumber,
  totalSteps,
  variant,
  title,
  isPreview,
}: ClaySoundStepProps) => {
  const { registerSlide } = useLessonPlayer();
  const { sendAlert } = useUi();

  const playerRef = useRef<AudioPlayer>(null);
  const [playing, setPlaying] = useState(false);
  const [playedOnce, setPlayedOnce] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);

  const mediaUrl = useAssetCdn(
    sound,
    SymbolType.Sound,
    CdnObjectType.Audio,
    variant === 'picture',
  );

  useEffect(() => {
    registerSlide(
      stepIdentifier,
      undefined,
      undefined,
      undefined,
      undefined,
      Math.round((stepNumber / totalSteps) * 100),
    );
  }, [registerSlide, stepIdentifier, stepNumber, totalSteps]);

  const hear = () => {
    playerRef.current?.play();
    setPlayedOnce(true);
  };

  return (
    <CarouselSlide
      identifier={stepIdentifier}
      className="theme-app relative"
      classNameInner="flex h-full w-full flex-col items-center justify-center pt-8 pb-40 md:pb-44"
      ariaLabel={`${activity.type.name} — ${stepNumber} of ${totalSteps}`}
      useContainer={false}
    >
      {/* gold clay field */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(40% 48% at 12% 14%, rgba(255,255,255,.30), transparent 70%),' +
            'radial-gradient(42% 42% at 90% 90%, rgba(255,255,255,.18), transparent 70%),' +
            '#FEE197',
        }}
      />

      <div className="relative flex flex-col items-center gap-4 px-8">
        {/* title */}
        <div
          style={{
            fontFamily: CLAY_FONT,
            fontWeight: 800,
            fontSize: 'clamp(30px,3.4vw,54px)',
            letterSpacing: '.5px',
            color: '#173A26',
            textAlign: 'center',
          }}
        >
          {title}
          {isPreview ? <span style={{ opacity: 0.4 }}> · Preview</span> : null}
        </div>

        {/* tappable cream flashcard */}
        <div
          role="button"
          tabIndex={0}
          aria-label={`Hear the sound`}
          onClick={hear}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              hear();
            }
          }}
          className={playedOnce ? undefined : 'clay-invite'}
          style={{
            width: 'clamp(240px,26vw,360px)',
            height: 'clamp(240px,26vw,360px)',
            background: 'var(--clay-card-white)',
            borderRadius: 44,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            overflow: 'hidden',
            boxShadow: playing
              ? '0 22px 44px -12px rgba(150,110,20,.32), 0 6px 14px rgba(120,90,20,.14), 0 0 0 12px rgba(42,128,73,.28)'
              : '0 22px 44px -12px rgba(150,110,20,.32), 0 6px 14px rgba(120,90,20,.14)',
            transition: 'box-shadow 160ms var(--ease)',
          }}
        >
          {variant === 'grapheme' ? (
            <span
              style={{
                fontFamily: FLASHCARD_FONT,
                fontWeight: 700,
                fontSize:
                  sound.length > 2
                    ? 'clamp(90px,12vw,170px)'
                    : 'clamp(150px,20vw,260px)',
                lineHeight: 1,
                color: 'var(--clay-ink)',
              }}
            >
              {sound}
            </span>
          ) : (
            <CdnMedia
              className="block h-full w-auto"
              symbol={sound}
              symbolType={SymbolType.Sound}
              objectType={CdnObjectType.Image}
              width={280}
              height={280}
            />
          )}
        </div>

        {/* green clay play button, tucked under the card */}
        <button
          type="button"
          aria-label="Hear the sound"
          disabled={!playerReady}
          onClick={hear}
          className="clay-press clay-btn-green"
          style={{
            marginTop: -30,
            width: 88,
            height: 88,
            borderRadius: '50%',
            border: 0,
            background: 'var(--clay-green)',
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            cursor: playerReady ? 'pointer' : 'not-allowed',
            opacity: playerReady ? 1 : 0.4,
            transform: playing ? 'scale(1.06)' : 'none',
            transition: 'transform 120ms var(--ease-bounce)',
          }}
        >
          <svg width="44" height="44" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5.2v13.6L19 12z" />
          </svg>
        </button>
      </div>

      {/* calm guiding mascot + instruction bubble, bottom-left */}
      <div className="absolute bottom-6 left-10 flex items-end gap-4">
        <Character
          pose="point"
          width="clamp(120px,13vw,190px)"
          alt="Pip the parrot"
        />
        <div
          style={{
            position: 'relative',
            marginBottom: 40,
            maxWidth: 300,
            background: 'var(--clay-cream)',
            borderRadius: 26,
            padding: '16px 22px',
            fontFamily: CLAY_FONT,
            fontWeight: 600,
            fontSize: 'clamp(15px,1.4vw,22px)',
            lineHeight: 1.2,
            color: '#3a2a12',
            boxShadow: 'var(--clay-shadow-cream)',
          }}
        >
          Say the sound out loud.
          <br />
          Tap the card to hear it.
          <span
            style={{
              position: 'absolute',
              left: -13,
              bottom: 22,
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '12px 18px 12px 0',
              borderColor:
                'transparent var(--clay-cream) transparent transparent',
            }}
          />
        </div>
      </div>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onPlaySrc={() => setPlaying(true)}
        onEnded={() => setPlaying(false)}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </CarouselSlide>
  );
};
